import type { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'
import { CUSTOMERIO } from '@/config/constants'

/**
 * Customer.io web in-app messages. edge-user-api identifies the profile on
 * edgeUserId and owns every trait, so the browser identifies with that id alone.
 * Page events exist only so in-app page rules know the route (PostHog captures
 * pageviews), and go out only while a user is identified.
 *
 * Every call is a no-op without a write key, and the SDK chunk is fetched on the
 * first identify, so a build without the key never downloads it.
 */

// AnalyticsBrowser is thenable: keep it here and never await it or return it from
// a promise callback, or it unwraps and the buffered identify/page/reset are lost.
let client: AnalyticsBrowser | null = null
let loading: Promise<void> | null = null

// What the app wants (identifiedId, currentPath) against what the SDK was sent
// (sentId, sentPath). flush() reconciles them, so a logout that lands while the
// chunk loads sends nothing, and repeats of the same id or path are dropped.
let identifiedId: string | null = null
let currentPath: string | null = null
let sentId: string | null = null
let sentPath: string | null = null

function ensureClient(writeKey: string): Promise<void> {
  loading ??= import('@customerio/cdp-analytics-browser').then(
    ({ AnalyticsBrowser }) => {
      client = AnalyticsBrowser.load({ writeKey })
    },
    // A failed chunk fetch leaves in-app off for now; the next identify retries.
    () => {
      loading = null
    },
  )
  return loading
}

function flush() {
  if (!client || !identifiedId) return
  if (sentId !== identifiedId) {
    void client.identify(identifiedId)
    sentId = identifiedId
    sentPath = null
  }
  if (currentPath && currentPath !== sentPath) {
    // Bare page(): the SDK takes the url from window.location, which page rules match.
    void client.page()
    sentPath = currentPath
  }
}

/** `id` must be edgeUserId, the backend's profile id; any other id duplicates the profile. */
export function identifyCustomerIo(id: string | undefined) {
  const { writeKey } = CUSTOMERIO
  if (!writeKey || !id || id === identifiedId) return
  identifiedId = id
  void ensureClient(writeKey).then(flush)
}

export function resetCustomerIo() {
  identifiedId = null
  if (client && sentId) void client.reset()
  sentId = null
  sentPath = null
}

/** The route as pathname + search; the router feeds every change. */
export function setCustomerIoPath(path: string) {
  currentPath = path
  flush()
}
